import { Box, Button, Divider, Flex, Icon, IconButton, Text, useColorMode } from '@chakra-ui/core';
import { useObserver } from 'mobx-react';
import React from 'react';
import { MdAdd, MdClose, MdPlaylistPlay, MdShare } from 'react-icons/md';
import { AnimateKeyframes } from 'react-simple-animate';

import { useStores } from '../hooks/useStores';
import { UserRoles } from '../types/shared';
import BrowseMusicPanel from './BrowseMusicPanel';
import ConfigPanel from './ConfigPanel';
import CurrentTrack from './CurrentTrack';
import Header from './Header';
import PlayerControls from './PlayerControls';
import QueueItem from './QueueItem';
import QueuePanel from './QueuePanel';
import SharePanel from './SharePanel';

function useStoreData() {
    const { store } = useStores();
    return useObserver(() => ({
        profile: store.me.profile,
        signOut: store.auth.signOut,
        isPlayerReady: store.playback.isPlayerReady,
        footerHeight: store.config.footerHeight,
        headerHeight: store.config.headerHeight,
        isHost: store.auth.role === UserRoles.HOST,
        isTopPanelOpen: store.config.isTopPanelOpen,
        isBottomPanelOpen: store.config.isBottomPanelOpen,
        isSharePanelOpen: store.config.isSharePanelOpen,
        isBrowseMusicPanelOpen: store.config.isBrowseMusicPanelOpen,
        isConfigPanelOpen: store.config.isConfigPanelOpen,
        toggleBrowseMusicPanel: store.config.toggleBrowseMusicPanel,
        toggleSharePanel: store.config.toggleSharePanel,
        toggleConfigPanel: store.config.toggleConfigPanel,
        toggleQueuePanelOnMobile: store.config.toggleQueuePanelOnMobile,
        closeAllPanels: store.config.closeAllPanels,
        setSearchTerm: store.search.setSearchTerm,
        isQueuePanelOpenOnMobile: store.config.isQueuePanelOpenOnMobile,
        upNext: store.playback.upNext,
    }));
}

const Main = () => {
    const {
        profile,
        signOut,
        isPlayerReady,
        footerHeight,
        headerHeight,
        isHost,
        isBrowseMusicPanelOpen,
        isSharePanelOpen,
        isConfigPanelOpen,
        toggleSharePanel,
        toggleQueuePanelOnMobile,
        toggleBrowseMusicPanel,
        closeAllPanels,
        isTopPanelOpen,
        isBottomPanelOpen,
        setSearchTerm,
        isQueuePanelOpenOnMobile,
        upNext,
    } = useStoreData();
    const { colorMode } = useColorMode();
    const color = { light: 'gray.800', dark: 'gray.50' };

    return (
        <Flex
            minH={[isQueuePanelOpenOnMobile ? '100vh' : '100vh', 'auto']}
            position="relative"
            // transition="height .3s"
            w="100%"
            flex={1}
            direction="column"
            alignItems="center"
            justifyContent="center"
        >
            <Flex
                p="1rem"
                pb={footerHeight}
                pt={headerHeight}
                position="absolute"
                transition="transform .3s"
                transform={`translateY(${isBottomPanelOpen ? '0' : '-100%'})`}
                right="0"
                bottom="0"
                top="0"
                zIndex={1}
                left="0"
                flexDirection="column"
            >
                <Flex position="relative" flex={1} overflow="hidden">
                    {isQueuePanelOpenOnMobile && <QueuePanel />}
                    {isSharePanelOpen && <SharePanel />}
                </Flex>

                {isBottomPanelOpen && (
                    <IconButton
                        variant="ghost"
                        fontSize="30px"
                        justifySelf="flex-end"
                        onClick={() => {
                            closeAllPanels();
                            setSearchTerm('');
                        }}
                        aria-label="Close panel"
                        icon={MdClose}
                    />
                )}
            </Flex>
            <Flex
                p="1rem"
                pb={footerHeight}
                pt={headerHeight}
                position="absolute"
                transition="transform .3s"
                top="0"
                transform={`translateY(${isTopPanelOpen ? '0' : '-100%'})`}
                right="0"
                bottom="0"
                zIndex={1}
                left="0"
                flexDirection="column"
            >
                <Flex position="relative" flex={1} overflow="hidden">
                    {isBrowseMusicPanelOpen && <BrowseMusicPanel />}
                    {isConfigPanelOpen && <ConfigPanel />}
                </Flex>

                {isTopPanelOpen && (
                    <IconButton
                        variant="ghost"
                        fontSize="30px"
                        justifySelf="flex-end"
                        onClick={() => {
                            closeAllPanels();
                            setSearchTerm('');
                        }}
                        aria-label="Close panel"
                        icon={MdClose}
                    />
                )}
            </Flex>
            <Flex position="relative" zIndex={1} w="100%" h={headerHeight} p=".5rem 1rem">
                {profile && <Header profile={profile} signOut={signOut} />}
            </Flex>
            <Flex
                className={`${(isTopPanelOpen || isBottomPanelOpen) && 'blur-filter'}`}
                justifyContent="center"
                transition="filter .3s"
                w="100%"
                flex={1}
                alignItems={['center']}
                direction="column"
            >
                <CurrentTrack />
                <Flex
                    justifySelf="flex-end"
                    alignSelf="flex-end"
                    h="66px"
                    minH="66px"
                    opacity={0.7}
                    p="0"
                    w="100%"
                    direction="column"
                    justifyContent="center"
                    position="relative"
                    display={['flex', 'flex', 'none']}
                >
                    {upNext ? (
                        <Flex onClick={toggleQueuePanelOnMobile} alignItems="center" className="background-transparent">
                            <Box flex={1}>
                                <QueueItem item={upNext} />
                            </Box>
                            <Box position="absolute" right={'1rem'} top={'.8rem'}>
                                <AnimateKeyframes
                                    duration={0.8}
                                    direction="alternate"
                                    keyframes={['opacity: 0', 'opacity: 1']}
                                    iterationCount="infinite"
                                >
                                    <Text fontSize=".8rem">UP NEXT</Text>
                                </AnimateKeyframes>
                            </Box>
                        </Flex>
                    ) : (
                        <Flex
                            onClick={toggleBrowseMusicPanel}
                            w="100%"
                            justifyContent="center"
                            alignItems="center"
                            p="0 1rem"
                            flex={1}
                            className="background-transparent"
                        >
                            <Text fontSize="20px" mr=".5rem">
                                The queue is empty
                            </Text>
                            <Box as={MdAdd} fontSize="30px" />
                        </Flex>
                    )}
                </Flex>
            </Flex>
            <Flex zIndex={1} w="100%" direction="column" p=".5rem 1rem" h={footerHeight} justifyContent="space-between">
                <Divider opacity={0.2} mt="0" mb=".5rem" minW={'100%'} />
                <Flex w="100%" flex={1} justifyContent="space-between" direction="row-reverse">
                    {/* {!isTopPanelOpen && ( */}
                    {/* <Flex> */}
                    {/* TODO: Settings panel */}
                    {/* <IconButton
                                size="lg"
                                fontSize="30px"
                                variant="ghost"
                                color={color[colorMode]}
                                aria-label="Settings"
                                icon={MdSettings}
                                onClick={toggleConfigPanel}
                            /> */}
                    {/* </Flex> */}
                    {/* )} */}

                    <IconButton
                        aria-label="Share"
                        fontSize="30px"
                        size="lg"
                        alignSelf="flex-end"
                        variant="ghost"
                        icon={MdShare}
                        color={color[colorMode]}
                        onClick={toggleSharePanel}
                    />
                    {isHost && isPlayerReady && (
                        <Flex>
                            <PlayerControls />
                        </Flex>
                    )}
                    <IconButton
                        display={['block', 'block', 'none']}
                        aria-label="Queue"
                        fontSize="30px"
                        size="lg"
                        alignSelf="flex-end"
                        variant="ghost"
                        icon={MdPlaylistPlay}
                        color={color[colorMode]}
                        onClick={toggleQueuePanelOnMobile}
                    />
                    {/*  TODO: is it a '+' button necessary?
                          <IconButton
                             onClick={toggleBrowseMusicPanel}
                             color={color[colorMode]}
                             icon={MdAdd}
                             size="lg"
                             fontSize="35px"
                             aria-label="Add"
                             variant="ghost"
                         /> */}
                </Flex>
            </Flex>
        </Flex>
    );
};

export default Main;
